import { createSlice } from "@reduxjs/toolkit";
import { axiosInstance } from "../../../../Common/Interceptors/AxiosConfig";
import { showNotification } from "../../../../Common/Toast/Toast";

const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

const loadCachedWorkspaces = () => {
  try {
    const cached = localStorage.getItem('bkz_cached_workspaces');
    return cached ? JSON.parse(cached) : [];
  } catch (e) { return []; }
};

const initialState = {
  errors: null,
  isLoading: false,
  currentWorkspace: null,

  // Cache for detailed info used in Dashboards
  detailsByWorkspaceId: {},  // { workspaceId: detailsObject }
  detailsFetchedAt: {},      // { workspaceId: timestamp }

  // Legacy single reference (kept for compatibility during migration)
  workspaceDetails: null,

  myWorkspaces: loadCachedWorkspaces(),
  createSuccess: false,
  lastFetchedAt: null,
};

const WorkspaceSlice = createSlice({
  name: "workspace",
  initialState,
  reducers: {
    setErrors: (state, action) => {
      state.errors = action.payload;
    },
    setLoading: (state, action) => {
      state.isLoading = action.payload;
    },
    setCurrentWorkspace: (state, action) => {
      state.currentWorkspace = action.payload;
    },
    setMyWorkspaces: (state, action) => {
      state.myWorkspaces = action.payload;
      state.lastFetchedAt = Date.now();
    },
    setWorkspaceDetails: (state, action) => {
      const details = action.payload;
      if (!details) return;

      const wsId = String(details._id || details.id);
      state.detailsByWorkspaceId[wsId] = details;
      state.detailsFetchedAt[wsId] = Date.now();

      // Also set the legacy property
      state.workspaceDetails = details;
      state.isLoading = false;
    },
    setCreateSuccess: (state, action) => {
      state.createSuccess = action.payload;
    },
    setLastFetched: (state) => {
      state.lastFetchedAt = Date.now();
    },
    updateMyWorkspaceInList: (state, action) => {
      const updatedWorkspace = action.payload;
      if (Array.isArray(state.myWorkspaces)) {
        state.myWorkspaces = state.myWorkspaces.map(ws =>
          ws._id === updatedWorkspace._id || ws.id === updatedWorkspace._id ? { ...ws, ...updatedWorkspace } : ws
        );
      }
    }
  },
});

/**
 * Create a new Workspace (Unified)
 */
export const createWorkspace = (formData) => async (dispatch) => {
  dispatch(setLoading(true));
  dispatch(setCreateSuccess(false));
  try {
    const URL = "workspaces/me";
    const response = await axiosInstance.post(URL, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    const { success, data, message } = response?.data || {};

    if (success) {
      dispatch(setCurrentWorkspace(data));
      dispatch(setCreateSuccess(true));
      showNotification({
        message: message || "Workspace created successfully",
        type: "success",
      });
      dispatch(listMyWorkspaces(true));
      return { success: true, data };
    }
  } catch (error) {
    const errorMsg = error?.response?.data?.message || "Failed to create workspace";
    showNotification({ message: errorMsg, type: "error" });
    return { success: false, message: errorMsg };
  } finally {
    dispatch(setLoading(false));
  }
};

/**
 * List all workspaces owned by the user
 */
export const listMyWorkspaces = (force = false) => async (dispatch, getState) => {
  const { myWorkspaces, lastFetchedAt, isLoading } = getState().workspace;
  const ONE_MINUTE = 60 * 1000;

  const hasContent = Array.isArray(myWorkspaces)
    ? myWorkspaces.length > 0
    : (myWorkspaces?.business?.length > 0 || myWorkspaces?.professional?.length > 0);

  if (
    !force &&
    hasContent &&
    lastFetchedAt &&
    Date.now() - lastFetchedAt < ONE_MINUTE
  ) return { success: true, data: myWorkspaces };

  // Skip loading spinner if we have cached data
  const hasCachedWorkspaces = myWorkspaces && (myWorkspaces.business?.length > 0 || myWorkspaces.professional?.length > 0);
  if (isLoading || hasCachedWorkspaces) {
    // silently continue
  } else {
    dispatch(setLoading(true));
  }

  try {
    const URL = "workspaces/my-workspaces";
    const response = await axiosInstance.get(URL);
    const { success, data } = response?.data || {};
    if (success) {
      dispatch(setMyWorkspaces(data));
      try {
        localStorage.setItem('bkz_cached_workspaces', JSON.stringify(data));

        // ✅ Self-Healing: If the user has zero workspaces, clear the active pointer
        const hasWorkspaces = (data.business?.length > 0 || data.professional?.length > 0);
        if (!hasWorkspaces) {
          localStorage.removeItem('bz_active_workspace_id');
        }
      } catch (e) { }
      return { success: true, data: { ...data } };
    } else {
      localStorage.removeItem('bkz_cached_workspaces');
      localStorage.removeItem('bz_active_workspace_id');
    }
  } catch (error) {
    if (error?.response?.status === 404) {
      localStorage.removeItem('bz_active_workspace_id');
      localStorage.removeItem('bkz_cached_workspaces');
    }
    return { success: false };
  } finally {
    dispatch(setLastFetched());
    dispatch(setLoading(false));
  }
};

/**
 * Fetch detailed information for a single workspace
 */
export const fetchWorkspaceDetails = (workspaceId, type, force = false) => async (dispatch, getState) => {
  if (!workspaceId) return;

  const state = getState().workspace;
  const fetchedAt = state.detailsFetchedAt[workspaceId];

  if (!force && fetchedAt && Date.now() - fetchedAt < CACHE_DURATION) return;

  dispatch(setLoading(true));
  try {
    const URL = `workspaces/${workspaceId}`;
    const response = await axiosInstance.get(URL);
    const { data } = response?.data || {};
    if (data) {
      dispatch(setWorkspaceDetails(data));
    }
  } catch (error) {
    console.error("fetchWorkspaceDetails error:", error);
    // ✅ Self-Healing: If workspace is deleted from DB, clear the pointer
    if (error?.response?.status === 404) {
      localStorage.removeItem('bz_active_workspace_id');
    }
    dispatch(setCurrentWorkspace(null));
  } finally {
    dispatch(setLoading(false));
  }
};

/**
 * Fetch workspace details by USERNAME (Public Discovery)
 */
export const fetchWorkspaceByUsername = (username) => async (dispatch) => {
  dispatch(setLoading(true));
  try {
    const URL = `discovery/username/${username}`;
    const response = await axiosInstance.get(URL);
    const { success, data } = response?.data || {};
    if (success && data) {
      dispatch(setWorkspaceDetails(data));
      return { success: true, data };
    }
  } catch (error) {
    console.error("fetchWorkspaceByUsername error:", error);
    return { success: false };
  } finally {
    dispatch(setLoading(false));
  }
};

/**
 * Update Workspace Profile Picture (Direct Upload)
 */
export const updateWorkspacePicture = (workspaceId, type, file) => async (dispatch) => {
  dispatch(setLoading(true));
  try {
    const formData = new FormData();
    formData.append('profilePicture', file);

    const URL = `workspaces/${workspaceId}`;

    const response = await axiosInstance.patch(URL, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });

    const { success, data } = response?.data || {};
    if (success) {
      // ✅ Update the local list immediately for instant UI feedback
      dispatch(updateMyWorkspaceInList(data));

      // Force refresh details
      dispatch(fetchWorkspaceDetails(workspaceId, type, true));
      dispatch(listMyWorkspaces(true));

      showNotification({
        message: "Profile picture updated successfully",
        type: "success",
      });
      return { success: true, data };
    }
  } catch (error) {
    const errorMsg = error?.response?.data?.message || "Failed to update profile picture";
    showNotification({ message: errorMsg, type: "error" });
    return { success: false, message: errorMsg };
  } finally {
    dispatch(setLoading(false));
  }
};

/**
 * Initiate Workspace Creation (Draft Generation)
 */
export const initiateWorkspace = (payload) => async (dispatch) => {
  dispatch(setLoading(true));
  try {
    const URL = "workspaces/initiate";
    const response = await axiosInstance.post(URL, payload);
    const { success, data } = response?.data || {};
    if (success) {
      return { success: true, data };
    }
  } catch (error) {
    console.error("initiateWorkspace error:", error);
    return { success: false };
  } finally {
    dispatch(setLoading(false));
  }
};

/**
 * Update Workspace Details (Incremental Draft Save)
 */
export const updateWorkspaceDetails = (workspaceId, type, details) => async (dispatch) => {
  try {
    const normalizedType = type.toLowerCase();
    const URL = `workspaces/${normalizedType}/${workspaceId}`;
    const response = await axiosInstance.patch(URL, details);
    if (response.data.success) {
      // Refresh details for this workspace
      dispatch(fetchWorkspaceDetails(workspaceId, type, true));
    }
    return response.data;
  } catch (error) {
    console.error("updateWorkspaceDetails error:", error);
    return { success: false };
  }
};

/**
 * Upload a Verification Document (Aadhar, PAN, Store Photo, etc.)
 * POST /workspaces/:id/verification/upload
 */
export const uploadWorkspaceDocument = (workspaceId, documentType, files) => async (dispatch) => {
  dispatch(setLoading(true));
  try {
    const formData = new FormData();
    formData.append('type', documentType);

    if (files.front) formData.append('frontImage', files.front);
    if (files.back) formData.append('backImage', files.back);
    if (files.images && files.images.length > 0) {
      files.images.forEach(img => formData.append('images', img));
    }

    const URL = `workspaces/${workspaceId}/verification/upload`;
    const response = await axiosInstance.post(URL, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });

    const { success, message, data } = response?.data || {};
    if (success) {
      showNotification({ message: message || `${documentType} uploaded successfully`, type: "success" });
      // Refresh details to show updated verification status
      const type = data?.personaType || 'Professional'; // Fallback if not in response
      dispatch(fetchWorkspaceDetails(workspaceId, type, true));
      return { success: true, data };
    }
  } catch (error) {
    const errorMsg = error?.response?.data?.message || `Failed to upload ${documentType}`;
    showNotification({ message: errorMsg, type: "error" });
    return { success: false, message: errorMsg };
  } finally {
    dispatch(setLoading(false));
  }
};

/**
 * Upload a Level-Specific Governance Document (Certification, License, etc.)
 * POST /workspaces/:id/governance/level-docs
 */
export const uploadLevelDocument = (workspaceId, documentType, targetLevel, files) => async (dispatch) => {
  dispatch(setLoading(true));
  try {
    const formData = new FormData();
    formData.append('type', documentType);
    formData.append('targetLevel', targetLevel);

    if (files.front) formData.append('frontImage', files.front);
    if (files.back) formData.append('backImage', files.back);
    if (files.images && files.images.length > 0) {
      files.images.forEach(img => formData.append('images', img));
    }

    const URL = `workspaces/${workspaceId}/governance/level-docs`;
    const response = await axiosInstance.post(URL, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });

    const { success, message, data } = response?.data || {};
    if (success) {
      showNotification({ message: message || `${documentType} uploaded for Level ${targetLevel}`, type: "success" });
      // Refresh details to show updated governance status
      dispatch(fetchWorkspaceDetails(workspaceId, 'professional', true)); // type is usually professional/business
      return { success: true, data };
    }
  } catch (error) {
    const errorMsg = error?.response?.data?.message || `Failed to upload ${documentType}`;
    showNotification({ message: errorMsg, type: "error" });
    return { success: false, message: errorMsg };
  } finally {
    dispatch(setLoading(false));
  }
};

/**
 * Finalize and Submit Level Verification Request
 * PATCH /workspaces/:type/:id
 */
export const submitLevelVerification = (workspaceId, type) => async (dispatch) => {
  dispatch(setLoading(true));
  try {
    const normalizedType = type.toLowerCase();
    const URL = `workspaces/${normalizedType}/${workspaceId}`;

    // We update the approvalStatus to pending to trigger admin review
    const response = await axiosInstance.patch(URL, {
      approvalStatus: 'pending'
    });

    const { success, message, data } = response?.data || {};
    if (success) {
      showNotification({
        message: message || "Level Verification Request Submitted!",
        type: "success"
      });
      dispatch(fetchWorkspaceDetails(workspaceId, type, true));
      return { success: true, data };
    }
  } catch (error) {
    const errorMsg = error?.response?.data?.message || "Failed to submit verification request";
    showNotification({ message: errorMsg, type: "error" });
    return { success: false, message: errorMsg };
  } finally {
    dispatch(setLoading(false));
  }
};

export const {
  setErrors,
  setLoading,
  setCurrentWorkspace,
  setMyWorkspaces,
  setWorkspaceDetails,
  setCreateSuccess,
  setLastFetched,
  updateMyWorkspaceInList,
} = WorkspaceSlice.actions;

export default WorkspaceSlice.reducer;
